import { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Search, Plus, Upload, Edit2, Trash2, ChevronDown, X, Check,
  AlertTriangle, Package, Tag, Scale, FileSpreadsheet, Merge, RefreshCw, TrendingUp,
  DollarSign, Info, Calendar, Building, ArrowRight, ChevronRight, Clock, ToggleLeft, ToggleRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ExcelMappingWizard from '../components/ExcelMappingWizard';
import QualityBadges, { buildAlertLookup, getBadgesForEntity } from '../components/intelligence/QualityBadges';

const IngredientMaster = () => {
  const { user } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState(''); // '' = all, 'uncategorized', or category_id UUID
  const [page, setPage] = useState(0);
  const [recomputingPrices, setRecomputingPrices] = useState(false);
  const limit = 20;
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showPricingDrawer, setShowPricingDrawer] = useState(false);
  const [showSyncConfirmModal, setShowSyncConfirmModal] = useState(false);
  const [pendingSyncToggle, setPendingSyncToggle] = useState(null); // { ingredient, newState }
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [pricingData, setPricingData] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  // Identity resolution state
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [resolutionData, setResolutionData] = useState(null); // { candidates, resolution_token, input_name }
  const [resolutionPending, setResolutionPending] = useState(false);
  const [togglingSync, setTogglingSync] = useState(null); // Track which ingredient is being toggled
  const [alertLookup, setAlertLookup] = useState({ byEntityId: {}, byIngredientName: {} });
  
  // Form state
  const [formData, setFormData] = useState({
    ingredient_name: '',
    ingredient_category: '',
    ingredient_base_unit: 'kg',
    aliases: [],
    item_type: 'INGREDIENT',
    category_id: '',
  });
  const [aliasInput, setAliasInput] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [itemCategories, setItemCategories] = useState([]);

  useEffect(() => {
    loadIngredients();
  }, [search, filterCategoryId, page]);

  // Load data quality alerts for inline badges
  useEffect(() => {
    api.getStructuredAlerts()
      .then((res) => setAlertLookup(buildAlertLookup(res.data?.alerts || [])))
      .catch((e) => console.error('Error loading quality alerts:', e));
  }, []);

  // Load item categories for the form dropdown
  useEffect(() => {
    api.getItemCategories({ active_only: true })
      .then((res) => setItemCategories(res.data?.categories || []))
      .catch((e) => console.error('Error loading item categories:', e));
  }, []);

  const loadIngredients = async () => {
    setLoading(true);
    try {
      const params = {
        search: search || undefined,
        limit,
        skip: page * limit,
      };
      if (filterCategoryId === 'uncategorized') {
        params.uncategorized = true;
      } else if (filterCategoryId) {
        params.category_id = filterCategoryId;
      }
      const response = await api.getCanonicalIngredients(params);
      setIngredients(response.data?.ingredients || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      console.error('Error loading ingredients:', error);
      setIngredients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.ingredient_name.trim()) {
      alert('Ingredient name is required');
      return;
    }
    
    setSaving(true);
    try {
      // Phase 1: Identity resolution
      const resolveRes = await api.resolveIngredient({ ingredient_name: formData.ingredient_name.trim() });
      const { candidates, resolution_token, has_candidates } = resolveRes.data;
      
      if (has_candidates) {
        // Candidates found — show resolution modal, pause creation
        setResolutionData({ candidates, resolution_token, input_name: formData.ingredient_name.trim() });
        setShowResolutionModal(true);
        setSaving(false);
        return;
      }
      
      // No candidates — safe to create directly
      await api.createCanonicalIngredient({
        ...formData,
        resolution_token,
        resolution_action: 'create_new_confirmed',
      });
      setShowCreateModal(false);
      resetForm();
      loadIngredients();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error creating ingredient');
    } finally {
      setSaving(false);
    }
  };

  const handleResolutionDecision = async (action, targetId) => {
    if (!resolutionData) return;
    setResolutionPending(true);
    try {
      await api.createCanonicalIngredient({
        ...formData,
        resolution_token: resolutionData.resolution_token,
        resolution_action: action,
        target_ingredient_id: targetId || undefined,
      });
      setShowResolutionModal(false);
      setShowCreateModal(false);
      setResolutionData(null);
      resetForm();
      loadIngredients();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error completing resolution');
    } finally {
      setResolutionPending(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedIngredient) return;
    
    setSaving(true);
    try {
      await api.updateCanonicalIngredient(selectedIngredient.ingredient_id, formData);
      setShowEditModal(false);
      setSelectedIngredient(null);
      resetForm();
      loadIngredients();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error updating ingredient');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.bulkUploadIngredients(formData);
      alert(`Upload complete: ${response.data.created} created, ${response.data.skipped_duplicates} skipped`);
      setShowUploadModal(false);
      loadIngredients();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error uploading file');
    } finally {
      setSaving(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleMerge = async () => {
    if (!selectedIngredient || selectedForMerge.length === 0) return;
    
    setSaving(true);
    try {
      await api.mergeIngredients(selectedIngredient.ingredient_id, selectedForMerge);
      setShowMergeModal(false);
      setSelectedIngredient(null);
      setSelectedForMerge([]);
      loadIngredients();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error merging ingredients');
    } finally {
      setSaving(false);
    }
  };

  // Handle sync toggle - show confirmation for OFF, immediate for ON
  const handleSyncToggle = (ingredient, e) => {
    e.stopPropagation();
    const currentState = ingredient.sync_to_inventory !== false; // Default to true
    const newState = !currentState;
    
    if (newState === false) {
      // Turning OFF - show confirmation modal
      setPendingSyncToggle({ ingredient, newState });
      setShowSyncConfirmModal(true);
    } else {
      // Turning ON - execute immediately
      executeSyncToggle(ingredient, newState);
    }
  };

  // Execute the sync toggle API call
  const executeSyncToggle = async (ingredient, newState) => {
    setTogglingSync(ingredient.ingredient_id);
    try {
      await api.updateCanonicalIngredient(ingredient.ingredient_id, { 
        sync_to_inventory: newState 
      });
      // Update local state immediately for responsiveness
      setIngredients(prev => prev.map(ing => 
        ing.ingredient_id === ingredient.ingredient_id 
          ? { ...ing, sync_to_inventory: newState }
          : ing
      ));
    } catch (error) {
      alert(error.response?.data?.detail || 'Error updating sync status');
    } finally {
      setTogglingSync(null);
      setShowSyncConfirmModal(false);
      setPendingSyncToggle(null);
    }
  };

  // Confirm turning sync OFF
  const confirmSyncOff = () => {
    if (pendingSyncToggle) {
      executeSyncToggle(pendingSyncToggle.ingredient, pendingSyncToggle.newState);
    }
  };

  const openEditModal = (ingredient) => {
    setSelectedIngredient(ingredient);
    setFormData({
      ingredient_name: ingredient.ingredient_name,
      ingredient_category: ingredient.ingredient_category || '',
      ingredient_base_unit: ingredient.ingredient_base_unit || 'kg',
      aliases: ingredient.aliases || [],
      item_type: ingredient.item_type || 'INGREDIENT',
      category_id: ingredient.category_id || '',
    });
    setShowEditModal(true);
  };

  const openMergeModal = (ingredient) => {
    setSelectedIngredient(ingredient);
    setSelectedForMerge([]);
    setShowMergeModal(true);
  };

  const openPricingDrawer = async (ingredient) => {
    setSelectedIngredient(ingredient);
    setShowPricingDrawer(true);
    setLoadingPricing(true);
    setPricingData(null);
    
    try {
      const response = await api.getIngredientPricing(ingredient.ingredient_id);
      setPricingData(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      // Show basic data from the ingredient if full fetch fails
      setPricingData({
        ingredient_id: ingredient.ingredient_id,
        ingredient_name: ingredient.ingredient_name,
        base_uom: ingredient.ingredient_base_unit,
        canonical_price_per_base_uom: ingredient.canonical_price_per_base_uom,
        canonical_price_source: ingredient.canonical_price_source,
        pricing_confidence: ingredient.pricing_confidence,
        seed_price_per_base_uom: ingredient.seed_price_per_base_uom,
        ...ingredient.price_stats
      });
    } finally {
      setLoadingPricing(false);
    }
  };

  const closePricingDrawer = () => {
    setShowPricingDrawer(false);
    setSelectedIngredient(null);
    setPricingData(null);
  };

  const handleRecomputePrices = async () => {
    setRecomputingPrices(true);
    try {
      const recomputeResponse = await api.recomputeAllPriceStats();
      const result = recomputeResponse.data;
      // Reload ingredients to show updated prices
      await loadIngredients();
      alert(`Price stats recomputed!\n\n• ${result.mapped_items_processed || 0} mapped lines processed\n• ${result.ingredients_updated || 0} ingredients updated\n• ${result.unique_ingredients || 0} unique ingredients found`);
    } catch (error) {
      console.error('Error recomputing prices:', error);
      alert('Failed to recompute prices: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRecomputingPrices(false);
    }
  };

  const resetForm = () => {
    setFormData({
      ingredient_name: '',
      ingredient_category: '',
      ingredient_base_unit: 'kg',
      aliases: [],
      item_type: 'INGREDIENT',
      category_id: '',
    });
    setAliasInput('');
  };

  const addAlias = () => {
    if (aliasInput.trim() && !formData.aliases.includes(aliasInput.trim())) {
      setFormData({ ...formData, aliases: [...formData.aliases, aliasInput.trim()] });
      setAliasInput('');
    }
  };

  const removeAlias = (alias) => {
    setFormData({ ...formData, aliases: formData.aliases.filter(a => a !== alias) });
  };

  // RBAC check
  if (!user?.is_org_admin) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertTriangle className="mx-auto text-red-600 mb-4" size={48} />
          <h2 className="text-xl font-bold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-700">Only organization administrators can manage the ingredient master.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="ingredient-master-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Ingredient Master</h1>
          <p className="text-gray-600 mt-1">Manage canonical ingredients for the organization</p>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="recompute-prices-btn"
            onClick={handleRecomputePrices}
            disabled={recomputingPrices}
            className="px-4 py-2 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={18} className={recomputingPrices ? 'animate-spin' : ''} />
            {recomputingPrices ? 'Refreshing...' : 'Refresh Price Intelligence'}
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            data-testid="open-upload-wizard-btn"
          >
            <Upload size={18} />
            Upload Excel
          </button>
          <button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={18} />
            Add Ingredient
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search ingredients or aliases..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filterCategoryId}
            onChange={(e) => { setFilterCategoryId(e.target.value); setPage(0); }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="ingredient-category-filter"
          >
            <option value="">All Categories</option>
            <option value="uncategorized">Uncategorized</option>
            {itemCategories
              .filter((c) => c.item_type === 'INGREDIENT')
              .map((c) => (
                <option key={c.category_id} value={c.category_id}>
                  {c.category_name}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Ingredients</p>
              <p className="text-xl font-bold text-gray-900">{total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Tag className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">With Aliases</p>
              <p className="text-xl font-bold text-gray-900">
                {ingredients.filter(i => i.aliases?.length > 0).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Scale className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Showing</p>
              <p className="text-xl font-bold text-gray-900">{ingredients.length} of {total}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Price Stats Info Banner */}
        <div className="px-6 py-3 bg-indigo-50 border-b border-indigo-100 flex items-center gap-2 text-sm text-indigo-700">
          <TrendingUp size={16} />
          <span>Price analytics (net amount incl. tax / landed cost) • Canonical mapped purchase lines from 1OS Bills</span>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : ingredients.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600">No ingredients found</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your search or add new ingredients</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Base Unit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      <div className="flex items-center gap-1">
                        Canonical Price
                        <Info size={12} className="text-gray-400" title="The authoritative price for costing" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      <div className="flex items-center justify-center gap-1">
                        Sync to Inventory
                        <Info size={12} className="text-gray-400" title="Controls whether ingredient syncs to Inventory Engine" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aliases</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {ingredients.map((ing) => (
                    <tr 
                      key={ing.ingredient_id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => openPricingDrawer(ing)}
                      data-testid={`ingredient-row-${ing.ingredient_id}`}
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{ing.ingredient_name}</p>
                        <p className="text-xs text-gray-500 font-mono">{ing.ingredient_id.slice(0, 8)}...</p>
                        <QualityBadges
                          badges={getBadgesForEntity(alertLookup, ing.ingredient_id, ing.ingredient_name)}
                          className="mt-1"
                        />
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const cat = ing.category_id && itemCategories.find((c) => c.category_id === ing.category_id);
                          const displayName = cat ? cat.category_name : ing.ingredient_category;
                          return (
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              displayName
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {displayName || 'Uncategorized'}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {ing.ingredient_base_unit || 'kg'}
                      </td>
                      {/* Canonical Price Column */}
                      <td className="px-6 py-4 text-sm">
                        {ing.canonical_price_per_base_uom ? (
                          <span className="font-semibold text-gray-900">
                            ₹{ing.canonical_price_per_base_uom.toFixed(2)}
                            <span className="text-xs text-gray-500 ml-1">/{ing.ingredient_base_unit || 'kg'}</span>
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs italic">No price</span>
                        )}
                      </td>
                      {/* Source Badge Column */}
                      <td className="px-6 py-4">
                        <SourceBadge source={ing.canonical_price_source} />
                      </td>
                      {/* Confidence Badge Column */}
                      <td className="px-6 py-4">
                        <ConfidenceBadge confidence={ing.pricing_confidence} />
                      </td>
                      {/* Sync to Inventory Toggle Column */}
                      <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <SyncToggle 
                          ingredient={ing}
                          onToggle={handleSyncToggle}
                          isLoading={togglingSync === ing.ingredient_id}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(ing.aliases || []).slice(0, 2).map((alias, idx) => (
                            <span key={idx} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                              {alias}
                            </span>
                          ))}
                          {(ing.aliases?.length > 2) && (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
                              +{ing.aliases.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); openPricingDrawer(ing); }}
                            className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                            title="View pricing details"
                            data-testid={`view-pricing-btn-${ing.ingredient_id}`}
                          >
                            <DollarSign size={16} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditModal(ing); }}
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openMergeModal(ing); }}
                            className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg"
                            title="Merge duplicates into this"
                          >
                            <Merge size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * limit >= total}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <IngredientFormModal
            title="Add New Ingredient"
            formData={formData}
            setFormData={setFormData}
            aliasInput={aliasInput}
            setAliasInput={setAliasInput}
            addAlias={addAlias}
            removeAlias={removeAlias}
            saving={saving}
            onSave={handleCreate}
            onClose={() => setShowCreateModal(false)}
            itemCategories={itemCategories}
          />
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && selectedIngredient && (
          <IngredientFormModal
            title={`Edit: ${selectedIngredient.ingredient_name}`}
            formData={formData}
            setFormData={setFormData}
            aliasInput={aliasInput}
            setAliasInput={setAliasInput}
            addAlias={addAlias}
            removeAlias={removeAlias}
            saving={saving}
            onSave={handleUpdate}
            onClose={() => { setShowEditModal(false); setSelectedIngredient(null); }}
            itemCategories={itemCategories}
          />
        )}
      </AnimatePresence>

      {/* Excel Mapping Wizard */}
      <AnimatePresence>
        {showResolutionModal && resolutionData && (
          <IngredientResolutionModal
            data={resolutionData}
            pending={resolutionPending}
            onDecision={handleResolutionDecision}
            onClose={() => { setShowResolutionModal(false); setResolutionData(null); }}
          />
        )}
      </AnimatePresence>

      {/* Excel Mapping Wizard */}
      <AnimatePresence>
        <ExcelMappingWizard
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onImportComplete={(result) => {
            loadIngredients();
            if (result.created > 0) {
              // Refresh the list after successful import
              setPage(0);
            }
          }}
          entityType="ingredient"
        />
      </AnimatePresence>

      {/* Merge Modal */}
      <AnimatePresence>
        {showMergeModal && selectedIngredient && (
          <MergeModal
            targetIngredient={selectedIngredient}
            allIngredients={ingredients}
            selectedForMerge={selectedForMerge}
            setSelectedForMerge={setSelectedForMerge}
            saving={saving}
            onMerge={handleMerge}
            onClose={() => { setShowMergeModal(false); setSelectedIngredient(null); setSelectedForMerge([]); }}
          />
        )}
      </AnimatePresence>

      {/* Pricing Detail Drawer */}
      <AnimatePresence>
        {showPricingDrawer && (
          <PricingDrawer
            ingredient={selectedIngredient}
            pricingData={pricingData}
            loading={loadingPricing}
            onClose={closePricingDrawer}
          />
        )}
      </AnimatePresence>

      {/* Sync Confirmation Modal */}
      <AnimatePresence>
        {showSyncConfirmModal && pendingSyncToggle && (
          <SyncConfirmModal
            ingredient={pendingSyncToggle.ingredient}
            onConfirm={confirmSyncOff}
            onCancel={() => { setShowSyncConfirmModal(false); setPendingSyncToggle(null); }}
            isLoading={togglingSync === pendingSyncToggle.ingredient.ingredient_id}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Identity Resolution Modal ──
const MATCH_TYPE_LABELS = {
  exact_name: 'Exact match',
  alias_match: 'Alias match',
  normalized_exact: 'Normalized match',
  alias_normalized: 'Alias (normalized)',
  similar: 'Similar',
};

const IngredientResolutionModal = ({ data, pending, onDecision, onClose }) => {
  const { candidates, input_name } = data;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto"
        data-testid="resolution-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Possible matches found</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={18} />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          You are creating <strong className="text-gray-900">"{input_name}"</strong>.
          The system found existing items that may be the same ingredient.
          Please choose an action.
        </p>

        {/* Candidates list */}
        <div className="space-y-2 mb-5" data-testid="resolution-candidates">
          {candidates.map((c, i) => (
            <div
              key={c.ingredient_id}
              className="border border-gray-200 rounded-lg p-3"
              data-testid={`resolution-candidate-${i}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-gray-900 text-sm">{c.ingredient_name}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">
                  {MATCH_TYPE_LABELS[c.match_type] || c.match_type} ({Math.round(c.confidence * 100)}%)
                </span>
              </div>
              {c.aliases && c.aliases.length > 0 && (
                <p className="text-xs text-gray-500 mb-2">
                  Aliases: {c.aliases.join(', ')}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => onDecision('use_existing', c.ingredient_id)}
                  disabled={pending}
                  className="px-3 py-1 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50"
                  data-testid={`use-existing-${i}`}
                >
                  Use this ingredient
                </button>
                <button
                  onClick={() => onDecision('add_as_alias', c.ingredient_id)}
                  disabled={pending}
                  className="px-3 py-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                  data-testid={`add-as-alias-${i}`}
                >
                  Add "{input_name}" as alias
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Create new anyway */}
        <div className="border-t border-gray-200 pt-4">
          <button
            onClick={() => onDecision('create_new_confirmed', null)}
            disabled={pending}
            className="w-full px-4 py-2.5 text-sm font-medium bg-gray-100 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            data-testid="create-new-confirmed"
          >
            {pending ? 'Processing...' : `None of these — create "${input_name}" as new ingredient`}
          </button>
          <p className="text-[10px] text-gray-400 mt-2 text-center">
            Only choose this if you are certain this is a genuinely different ingredient.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Ingredient Form Modal Component
const ITEM_TYPE_OPTIONS = [
  { key: 'INGREDIENT', label: 'Ingredient' },
  { key: 'PACKAGING', label: 'Packaging' },
  { key: 'CONSUMABLE', label: 'Consumable' },
  { key: 'EQUIPMENT', label: 'Equipment' },
  { key: 'CROCKERY_CUTLERY', label: 'Crockery & Cutlery' },
];

const IngredientFormModal = ({ 
  title, formData, setFormData, aliasInput, setAliasInput, 
  addAlias, removeAlias, saving, onSave, onClose, itemCategories = []
}) => {
  // Filter categories by selected item_type
  const filteredCategories = itemCategories.filter(
    (cat) => cat.item_type === (formData.item_type || 'INGREDIENT')
  );

  return (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.95 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.95 }}
      onClick={(e) => e.stopPropagation()}
      className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
          <X size={20} />
        </button>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Item Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.ingredient_name}
            onChange={(e) => setFormData({ ...formData, ingredient_name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Tomato"
            data-testid="ingredient-form-name"
          />
        </div>

        {/* Item Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
          <select
            value={formData.item_type || 'INGREDIENT'}
            onChange={(e) => setFormData({ ...formData, item_type: e.target.value, category_id: '' })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="ingredient-form-item-type"
          >
            {ITEM_TYPE_OPTIONS.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Category (dynamic, filtered by item_type) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <select
            value={formData.category_id || ''}
            onChange={(e) => {
              const cat = filteredCategories.find((c) => c.category_id === e.target.value);
              setFormData({
                ...formData,
                category_id: e.target.value,
                ingredient_category: cat ? cat.category_name : '',
              });
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="ingredient-form-category"
          >
            <option value="">Select Category</option>
            {filteredCategories.map((cat) => (
              <option key={cat.category_id} value={cat.category_id}>
                {cat.category_name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Base Unit</label>
          <select
            value={formData.ingredient_base_unit}
            onChange={(e) => setFormData({ ...formData, ingredient_base_unit: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="ingredient-form-unit"
          >
            <option value="kg">Kilogram (kg)</option>
            <option value="g">Gram (g)</option>
            <option value="l">Liter (l)</option>
            <option value="ml">Milliliter (ml)</option>
            <option value="piece">Piece</option>
            <option value="dozen">Dozen</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Aliases</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={aliasInput}
              onChange={(e) => setAliasInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAlias())}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Add alias..."
            />
            <button
              onClick={addAlias}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.aliases.map((alias, idx) => (
              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center gap-1">
                {alias}
                <button onClick={() => removeAlias(alias)} className="hover:text-blue-900">
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>
      
      <div className="flex gap-3 mt-6">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <>
              <Check size={18} />
              Save
            </>
          )}
        </button>
      </div>
    </motion.div>
  </motion.div>
  );
};

// Merge Modal Component
const MergeModal = ({ 
  targetIngredient, allIngredients, selectedForMerge, setSelectedForMerge, 
  saving, onMerge, onClose 
}) => {
  const toggleSelect = (id) => {
    if (selectedForMerge.includes(id)) {
      setSelectedForMerge(selectedForMerge.filter(i => i !== id));
    } else {
      setSelectedForMerge([...selectedForMerge, id]);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">Merge Duplicates</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-blue-800">
            <strong>Target:</strong> {targetIngredient.ingredient_name}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Selected ingredients will be merged into this one. Their names will become aliases.
          </p>
        </div>
        
        <p className="text-sm font-medium text-gray-700 mb-2">Select ingredients to merge:</p>
        
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {allIngredients
            .filter(i => i.ingredient_id !== targetIngredient.ingredient_id)
            .map((ing) => (
              <label 
                key={ing.ingredient_id}
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                  selectedForMerge.includes(ing.ingredient_id) 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedForMerge.includes(ing.ingredient_id)}
                  onChange={() => toggleSelect(ing.ingredient_id)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div>
                  <p className="font-medium text-gray-900">{ing.ingredient_name}</p>
                  {ing.aliases?.length > 0 && (
                    <p className="text-xs text-gray-500">Aliases: {ing.aliases.join(', ')}</p>
                  )}
                </div>
              </label>
            ))}
        </div>
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onMerge}
            disabled={saving || selectedForMerge.length === 0}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Merge size={18} />
                Merge {selectedForMerge.length} Selected
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// =========================================================================
// SYNC TOGGLE COMPONENT
// =========================================================================
const SyncToggle = ({ ingredient, onToggle, isLoading }) => {
  const isSyncOn = ingredient.sync_to_inventory !== false; // Default to true
  
  return (
    <button
      onClick={(e) => onToggle(ingredient, e)}
      disabled={isLoading}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
        isSyncOn 
          ? 'bg-green-500 focus:ring-green-500' 
          : 'bg-gray-300 focus:ring-gray-400'
      } ${isLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
      data-testid={`sync-toggle-${ingredient.ingredient_id}`}
      title={isSyncOn ? 'Sync enabled - Click to disable' : 'Sync disabled - Click to enable'}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${
          isSyncOn ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
        </span>
      )}
    </button>
  );
};

// =========================================================================
// SYNC CONFIRMATION MODAL
// =========================================================================
const SyncConfirmModal = ({ ingredient, onConfirm, onCancel, isLoading }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    onClick={onCancel}
  >
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
      className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
      data-testid="sync-confirm-modal"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-amber-100 rounded-full">
          <AlertTriangle className="text-amber-600" size={24} />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Turn off Inventory Sync?</h3>
      </div>
      
      <div className="mb-6">
        <p className="text-gray-700 mb-3">
          You are about to disable sync for <strong>{ingredient.ingredient_name}</strong>.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          <p>
            This ingredient will stop syncing to Inventory Engine. If stock still exists 
            in Inventory, the ingredient will remain there until quantity becomes 0. 
            After that, Inventory should deactivate/remove it.
          </p>
        </div>
      </div>
      
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="confirm-sync-off-btn"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <>
              <ToggleLeft size={18} />
              Turn Off Sync
            </>
          )}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

// =========================================================================
// SOURCE BADGE COMPONENT
// =========================================================================
const SourceBadge = ({ source }) => {
  if (!source) return <span className="text-gray-400 text-xs">-</span>;
  
  const config = {
    'avg_30d': { 
      label: '30D Avg', 
      className: 'bg-blue-100 text-blue-800 border-blue-200' 
    },
    'avg_all_time': { 
      label: 'All-Time Avg', 
      className: 'bg-orange-100 text-orange-800 border-orange-200' 
    },
    'seed_price': { 
      label: 'Seed Price', 
      className: 'bg-amber-100 text-amber-800 border-amber-200' 
    },
  };
  
  const { label, className } = config[source] || { 
    label: source, 
    className: 'bg-gray-100 text-gray-800 border-gray-200' 
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${className}`}>
      {label}
    </span>
  );
};

// =========================================================================
// CONFIDENCE BADGE COMPONENT
// =========================================================================
const ConfidenceBadge = ({ confidence }) => {
  if (!confidence) return <span className="text-gray-400 text-xs">-</span>;
  
  const config = {
    'HIGH': { 
      label: 'High', 
      className: 'bg-green-100 text-green-800 border-green-200',
      icon: '●'
    },
    'MEDIUM': { 
      label: 'Medium', 
      className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      icon: '◐'
    },
    'LOW': { 
      label: 'Low', 
      className: 'bg-red-100 text-red-800 border-red-200',
      icon: '○'
    },
  };
  
  const { label, className, icon } = config[confidence] || { 
    label: confidence, 
    className: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: '?'
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${className} flex items-center gap-1`}>
      <span>{icon}</span>
      {label}
    </span>
  );
};

// =========================================================================
// PRICING DRAWER COMPONENT
// =========================================================================
const PricingDrawer = ({ ingredient, pricingData, loading, onClose }) => {
  const [driftAlerts, setDriftAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  
  // Fetch drift alerts for this ingredient
  useEffect(() => {
    if (ingredient?.ingredient_id) {
      fetchDriftAlerts();
    }
  }, [ingredient?.ingredient_id]);
  
  const fetchDriftAlerts = async () => {
    if (!ingredient?.ingredient_id) return;
    setLoadingAlerts(true);
    try {
      const response = await api.getIngredientDriftAlerts(ingredient.ingredient_id, 5);
      setDriftAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error fetching drift alerts:', error);
    } finally {
      setLoadingAlerts(false);
    }
  };
  
  const formatPrice = (price, unit = 'kg') => {
    if (price == null) return '-';
    return `₹${price.toFixed(2)}/${unit}`;
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };
  
  const getSeverityConfig = (severity) => {
    const configs = {
      CRITICAL: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' },
      HIGH: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' },
      WARNING: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Warning' }
    };
    return configs[severity] || configs.WARNING;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end"
      onClick={onClose}
      data-testid="pricing-drawer-overlay"
    >
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl"
        data-testid="pricing-drawer"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Pricing Details</h2>
            <p className="text-sm text-gray-500">{ingredient?.ingredient_name}</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
            data-testid="close-pricing-drawer"
          >
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : pricingData ? (
          <div className="p-6 space-y-6">
            {/* Canonical Price Section */}
            <section className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-5 border border-indigo-100">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="text-indigo-600" size={20} />
                <h3 className="font-semibold text-gray-900">Canonical Price</h3>
              </div>
              
              <div className="flex items-end gap-3 mb-3">
                <span className="text-3xl font-bold text-gray-900">
                  {pricingData.canonical_price_per_base_uom 
                    ? `₹${pricingData.canonical_price_per_base_uom.toFixed(2)}`
                    : 'No price'
                  }
                </span>
                <span className="text-gray-500 text-lg mb-1">
                  /{pricingData.base_uom || 'kg'}
                </span>
              </div>
              
              <div className="flex gap-2 mb-3">
                <SourceBadge source={pricingData.canonical_price_source} />
                <ConfidenceBadge confidence={pricingData.pricing_confidence} />
              </div>
              
              {pricingData.canonical_price_updated_at && (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock size={12} />
                  Updated: {formatDate(pricingData.canonical_price_updated_at)}
                </p>
              )}
              
              {/* Confidence explanation */}
              <div className="mt-3 pt-3 border-t border-indigo-200">
                <p className="text-xs text-gray-600">
                  {pricingData.pricing_confidence === 'HIGH' && (
                    <span>● High confidence: Based on 5+ bill lines in the last 30 days</span>
                  )}
                  {pricingData.pricing_confidence === 'MEDIUM' && (
                    <span>◐ Medium confidence: Based on 2-4 bill lines (30d) or all-time average</span>
                  )}
                  {pricingData.pricing_confidence === 'LOW' && (
                    <span>○ Low confidence: Using seed price from Excel upload (no vendor bills)</span>
                  )}
                  {!pricingData.pricing_confidence && (
                    <span>No pricing data available</span>
                  )}
                </p>
              </div>
            </section>

            {/* Price History Section */}
            <section className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="text-blue-600" size={20} />
                <h3 className="font-semibold text-gray-900">Price History (from Bills)</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">7-Day Average</p>
                  <p className="font-medium text-gray-900">
                    {formatPrice(pricingData.avg_7d_per_base_uom, pricingData.base_uom)}
                  </p>
                  <p className="text-xs text-gray-400">{pricingData.price_line_count_7d || 0} lines</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">30-Day Average</p>
                  <p className="font-medium text-gray-900">
                    {formatPrice(pricingData.avg_30d_per_base_uom, pricingData.base_uom)}
                  </p>
                  <p className="text-xs text-gray-400">{pricingData.price_line_count_30d || 0} lines</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">90-Day Average</p>
                  <p className="font-medium text-gray-900">
                    {formatPrice(pricingData.avg_90d_per_base_uom, pricingData.base_uom)}
                  </p>
                  <p className="text-xs text-gray-400">{pricingData.price_line_count_90d || 0} lines</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">All-Time Average</p>
                  <p className="font-medium text-gray-900">
                    {formatPrice(pricingData.avg_all_time_per_base_uom, pricingData.base_uom)}
                  </p>
                  <p className="text-xs text-gray-400">{pricingData.price_line_count_all_time || 0} lines</p>
                </div>
              </div>
              
              {/* Min/Max */}
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Minimum Price</p>
                  <p className="font-medium text-green-700">
                    {formatPrice(pricingData.min_per_base_uom, pricingData.base_uom)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Maximum Price</p>
                  <p className="font-medium text-red-700">
                    {formatPrice(pricingData.max_per_base_uom, pricingData.base_uom)}
                  </p>
                </div>
              </div>
              
              <p className="text-xs text-gray-400 mt-3 italic">
                Note: These averages are calculated from vendor bills only. Seed prices are not included.
              </p>
            </section>

            {/* Seed Price Section */}
            <section className="bg-amber-50 rounded-xl border border-amber-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <FileSpreadsheet className="text-amber-600" size={20} />
                <h3 className="font-semibold text-gray-900">Seed Price</h3>
                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Fallback</span>
              </div>
              
              {pricingData.seed_price_per_base_uom ? (
                <>
                  <p className="text-2xl font-bold text-gray-900 mb-2">
                    ₹{pricingData.seed_price_per_base_uom.toFixed(2)}
                    <span className="text-gray-500 text-base font-normal ml-1">/{pricingData.base_uom || 'kg'}</span>
                  </p>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>Source: {pricingData.seed_price_source || 'Excel upload'}</p>
                    {pricingData.seed_price_timestamp && (
                      <p>Uploaded: {formatDate(pricingData.seed_price_timestamp)}</p>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-gray-500 italic">No seed price uploaded</p>
              )}
              
              <p className="text-xs text-amber-700 mt-3">
                Seed price is used as a fallback when no vendor bill data is available.
              </p>
            </section>

            {/* Price Drift Alerts Section */}
            {driftAlerts.length > 0 && (
              <section className="bg-red-50 rounded-xl border border-red-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="text-red-600" size={20} />
                  <h3 className="font-semibold text-gray-900">Price Drift Alerts</h3>
                  <span className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded-full">
                    {driftAlerts.filter(a => a.status === 'OPEN').length} Open
                  </span>
                </div>
                
                <div className="space-y-3">
                  {driftAlerts.slice(0, 3).map((alert) => {
                    const sevConfig = getSeverityConfig(alert.deviation_severity);
                    const deviationPct = alert.deviation_pct * 100;
                    
                    return (
                      <div 
                        key={alert.alert_id}
                        className={`p-3 rounded-lg border ${alert.status === 'RESOLVED' ? 'bg-gray-50 opacity-60' : 'bg-white'}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${sevConfig.bg} ${sevConfig.text}`}>
                            {sevConfig.label}
                          </span>
                          <span className="text-xs text-gray-500">{formatDate(alert.bill_date)}</span>
                        </div>
                        <p className="text-sm text-gray-700">
                          Billed at <strong>₹{alert.actual_price_per_base_uom?.toFixed(2)}</strong>
                          {' vs '}
                          <strong>₹{alert.canonical_price_per_base_uom?.toFixed(2)}</strong>
                          {' — '}
                          <span className={alert.direction === 'up' ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                            {deviationPct > 0 ? '+' : ''}{deviationPct.toFixed(1)}%
                          </span>
                        </p>
                        {alert.vendor_name && (
                          <p className="text-xs text-gray-500 mt-1">Vendor: {alert.vendor_name}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {driftAlerts.length > 3 && (
                  <a 
                    href="/price-alerts" 
                    className="block mt-3 text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    View all {driftAlerts.length} alerts →
                  </a>
                )}
              </section>
            )}

            {/* Last Procurement Snapshot */}
            <section className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Building className="text-gray-600" size={20} />
                <h3 className="font-semibold text-gray-900">Last Procurement</h3>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Price</span>
                  <span className="font-medium text-gray-900">
                    {formatPrice(pricingData.last_per_base_uom, pricingData.base_uom)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Bill Date</span>
                  <span className="font-medium text-gray-900">
                    {formatDate(pricingData.last_bill_date)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Vendor</span>
                  <span className="font-medium text-gray-900">
                    {pricingData.last_vendor_name || '-'}
                  </span>
                </div>
              </div>
            </section>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No pricing data available
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default IngredientMaster;
